import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import api from '../utils/api';
import './ServicesConfig.css';

const ServicesConfig = () => {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.user_type !== 'coach';

  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Lookup data from backend
  const [rateTypes, setRateTypes] = useState([]);
  const [discountTypes, setDiscountTypes] = useState([]);
  const [categoryIcons, setCategoryIcons] = useState([]);

  // Derived lookup maps for display
  const rateLabels = {};
  const rateIcons = {};
  rateTypes.forEach(rt => { rateLabels[rt.key] = rt.label; rateIcons[rt.key] = rt.icon; });
  const catIconMap = {};
  categoryIcons.forEach(ci => { catIconMap[ci.key] = ci.emoji; });
  const discountSymbolMap = {};
  discountTypes.forEach(dt => { discountSymbolMap[dt.key] = dt.symbol || ''; });

  // Tab-based category selection (replaces nested accordions)
  const [selectedCat, setSelectedCat] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});

  // Editing state
  const [editingRate, setEditingRate] = useState(null);
  const [addingRateFor, setAddingRateFor] = useState(null);
  const [newRate, setNewRate] = useState({ rate_type: '', rate_amount: '', discount_type: '', discount_value: 0 });
  const [addingItem, setAddingItem] = useState(null);
  const [newItem, setNewItem] = useState({ name: '', description: '' });
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', icon: '' });
  const [editingItem, setEditingItem] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      try { await api.post('/api/services/init'); } catch (e) { /* tables may already exist */ }
      const res = await api.get('/api/services/all');
      const cats = res.data.categories || [];
      setCategories(cats);
      setItems(res.data.items || []);
      setRates(res.data.rates || []);
      const lookups = res.data.lookups || {};
      setRateTypes(lookups.rate_types || []);
      setDiscountTypes(lookups.discount_types || []);
      setCategoryIcons(lookups.category_icons || []);
      // Auto-select first category, or keep current if still valid
      setSelectedCat(prev => {
        if (prev && cats.find(c => c.id === prev)) return prev;
        return cats.length > 0 ? cats[0].id : null;
      });
    } catch (err) {
      setError('Failed to load services data');
    } finally {
      setLoading(false);
    }
  };

  const showMsg = (msg) => { setSuccess(msg); setError(''); setTimeout(() => setSuccess(''), 3000); };

  // Category CRUD
  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) return;
    try {
      await api.post('/api/services/categories', newCategory);
      showMsg('Category created');
      setAddingCategory(false);
      setNewCategory({ name: '', description: '', icon: '' });
      fetchAll();
    } catch (err) { setError(err.response?.data?.error || 'Failed to create category'); }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Delete this service category and all its items/rates?')) return;
    try {
      await api.delete(`/api/services/categories/${id}`);
      showMsg('Category deleted');
      fetchAll();
    } catch (err) { setError(err.response?.data?.error || 'Failed to delete'); }
  };

  // Item CRUD
  const handleAddItem = async (categoryId) => {
    if (!newItem.name.trim()) return;
    try {
      await api.post('/api/services/items', { category_id: categoryId, ...newItem });
      showMsg('Service item created');
      setAddingItem(null);
      setNewItem({ name: '', description: '' });
      fetchAll();
    } catch (err) { setError(err.response?.data?.error || 'Failed to create item'); }
  };

  const handleUpdateItem = async (item) => {
    try {
      await api.put(`/api/services/items/${item.id}`, { name: editingItem.name, description: editingItem.description, is_active: item.is_active });
      showMsg('Item updated');
      setEditingItem(null);
      fetchAll();
    } catch (err) { setError(err.response?.data?.error || 'Failed to update item'); }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Delete this service item and its rates?')) return;
    try {
      await api.delete(`/api/services/items/${id}`);
      showMsg('Item deleted');
      fetchAll();
    } catch (err) { setError(err.response?.data?.error || 'Failed to delete item'); }
  };

  // Rate CRUD
  const handleAddRate = async (itemId) => {
    if (!newRate.rate_amount) return;
    try {
      await api.post('/api/services/rates', { service_item_id: itemId, ...newRate, rate_amount: parseFloat(newRate.rate_amount), discount_value: parseFloat(newRate.discount_value) || 0 });
      showMsg('Rate added');
      setAddingRateFor(null);
      setNewRate({ rate_type: '', rate_amount: '', discount_type: '', discount_value: 0 });
      fetchAll();
    } catch (err) { setError(err.response?.data?.error || 'Failed to add rate'); }
  };

  // Image upload
  const handleImageUpload = async (entityType, entityId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      setUploadingImage(`${entityType}-${entityId}`);
      await api.post(`/api/services/upload-image/${entityType}/${entityId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showMsg('Image uploaded');
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload image');
    } finally {
      setUploadingImage(null);
    }
  };

  const handleUpdateRate = async (rate) => {
    try {
      await api.put(`/api/services/rates/${rate.id}`, {
        rate_type: editingRate.rate_type, rate_amount: parseFloat(editingRate.rate_amount),
        discount_type: editingRate.discount_type, discount_value: parseFloat(editingRate.discount_value) || 0,
        is_active: rate.is_active
      });
      showMsg('Rate updated');
      setEditingRate(null);
      fetchAll();
    } catch (err) { setError(err.response?.data?.error || 'Failed to update rate'); }
  };

  const handleDeleteRate = async (id) => {
    try {
      await api.delete(`/api/services/rates/${id}`);
      showMsg('Rate deleted');
      fetchAll();
    } catch (err) { setError(err.response?.data?.error || 'Failed to delete rate'); }
  };

  const getItemRates = (itemId) => rates.filter(r => r.service_item_id === itemId);
  const getCategoryItems = (catId) => items.filter(i => i.category_id === catId);

  const calcEffective = (rate) => {
    if (!rate.discount_type || !rate.discount_value) return rate.rate_amount;
    if (rate.discount_type === 'percent') return rate.rate_amount * (1 - rate.discount_value / 100);
    if (rate.discount_type === 'flat') return Math.max(0, rate.rate_amount - rate.discount_value);
    return rate.rate_amount;
  };

  const activeCat = categories.find(c => c.id === selectedCat);
  const activeCatItems = selectedCat ? getCategoryItems(selectedCat) : [];
  const activeCatIcon = activeCat ? (catIconMap[activeCat.icon] || '📦') : '';

  if (loading) {
    return (
      <div className="sv-loading">
        <div className="sv-spinner" />
        <p>Loading services...</p>
      </div>
    );
  }

  return (
    <div className="sv-root">
      {/* Toasts */}
      {error && (
        <div className="sv-toast sv-toast-err">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
          <span>{error}</span>
          <button className="sv-toast-close" onClick={() => setError('')}>×</button>
        </div>
      )}
      {success && (
        <div className="sv-toast sv-toast-ok">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          <span>{success}</span>
        </div>
      )}

      {/* Header */}
      <div className="sv-header">
        <div className="sv-header-left">
          <h2 className="sv-title">Services & Pricing</h2>
          <p className="sv-subtitle">Manage categories, services, and rate plans</p>
        </div>
        <div className="sv-header-right">
          <div className="sv-stats">
            <div className="sv-stat"><span className="sv-stat-num">{categories.length}</span><span className="sv-stat-label">Categories</span></div>
            <div className="sv-stat"><span className="sv-stat-num">{items.length}</span><span className="sv-stat-label">Services</span></div>
            <div className="sv-stat"><span className="sv-stat-num">{rates.length}</span><span className="sv-stat-label">Rates</span></div>
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="sv-tabs-bar">
        <div className="sv-tabs-scroll">
          {categories.map(cat => {
            const count = getCategoryItems(cat.id).length;
            return (
              <button key={cat.id} className={`sv-tab ${selectedCat === cat.id ? 'sv-tab-active' : ''}`} onClick={() => setSelectedCat(cat.id)}>
                <span className="sv-tab-icon">
                  {cat.image_url ? <img src={cat.image_url} alt="" className="sv-tab-img" /> : (catIconMap[cat.icon] || '📦')}
                </span>
                <span className="sv-tab-name">{cat.name}</span>
                <span className="sv-tab-count">{count}</span>
              </button>
            );
          })}
        </div>
        {isAdmin && (
          <button className="sv-add-tab" onClick={() => setAddingCategory(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Category
          </button>
        )}
      </div>

      {/* Add Category Form */}
      {addingCategory && (
        <div className="sv-panel">
          <div className="sv-panel-header">
            <h4>Create New Category</h4>
          </div>
          <div className="sv-panel-body">
            <div className="sv-form-grid">
              <div className="sv-field">
                <label className="sv-label">Name</label>
                <input type="text" placeholder="e.g. Group Classes" value={newCategory.name} onChange={e => setNewCategory({ ...newCategory, name: e.target.value })} className="sv-input" autoFocus />
              </div>
              <div className="sv-field sv-field-grow">
                <label className="sv-label">Description</label>
                <input type="text" placeholder="Brief description" value={newCategory.description} onChange={e => setNewCategory({ ...newCategory, description: e.target.value })} className="sv-input" />
              </div>
              <div className="sv-field">
                <label className="sv-label">Icon</label>
                <select value={newCategory.icon} onChange={e => setNewCategory({ ...newCategory, icon: e.target.value })} className="sv-input sv-select">
                  <option value="">Choose...</option>
                  {categoryIcons.map(ci => <option key={ci.key} value={ci.key}>{ci.emoji} {ci.label}</option>)}
                </select>
              </div>
            </div>
            <div className="sv-form-actions">
              <button className="sv-btn sv-btn-primary" onClick={handleAddCategory}>Create Category</button>
              <button className="sv-btn sv-btn-ghost" onClick={() => { setAddingCategory(false); setNewCategory({ name: '', description: '', icon: '' }); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Active Category Content */}
      {activeCat ? (
        <div className="sv-content">
          {/* Category Strip */}
          <div className="sv-cat-strip">
            <div className="sv-cat-strip-left">
              <div className="sv-cat-avatar" onClick={e => e.stopPropagation()}>
                {activeCat.image_url ? (
                  <img src={activeCat.image_url} alt={activeCat.name} className="sv-cat-avatar-img" />
                ) : (
                  <span className="sv-cat-avatar-emoji">{activeCatIcon}</span>
                )}
                {isAdmin && (
                  <label className="sv-avatar-overlay" title="Change image">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
                    <input type="file" accept="image/*" className="sv-hidden-input" onChange={e => handleImageUpload('category', activeCat.id, e.target.files[0])} />
                  </label>
                )}
                {uploadingImage === `category-${activeCat.id}` && <div className="sv-avatar-spinner" />}
              </div>
              <div>
                <h3 className="sv-cat-strip-name">{activeCat.name}</h3>
                {activeCat.description && <p className="sv-cat-strip-desc">{activeCat.description}</p>}
              </div>
            </div>
            <div className="sv-cat-strip-right">
              <span className="sv-chip">{activeCatItems.length} service{activeCatItems.length !== 1 ? 's' : ''}</span>
              <span className="sv-chip sv-chip-accent">
                {activeCatItems.reduce((s, i) => s + getItemRates(i.id).length, 0)} rate{activeCatItems.reduce((s, i) => s + getItemRates(i.id).length, 0) !== 1 ? 's' : ''}
              </span>
              {isAdmin && (
                <button className="sv-btn-icon sv-btn-icon-danger" onClick={() => handleDeleteCategory(activeCat.id)} title="Delete category">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                </button>
              )}
            </div>
          </div>

          {/* Add Item Form */}
          {addingItem === selectedCat && (
            <div className="sv-panel">
              <div className="sv-panel-header"><h4>Add New Service</h4></div>
              <div className="sv-panel-body">
                <div className="sv-form-grid">
                  <div className="sv-field">
                    <label className="sv-label">Service Name</label>
                    <input type="text" placeholder="e.g. Budding" value={newItem.name} onChange={e => setNewItem({ ...newItem, name: e.target.value })} className="sv-input" autoFocus
                      onKeyDown={e => { if (e.key === 'Enter') handleAddItem(selectedCat); if (e.key === 'Escape') setAddingItem(null); }} />
                  </div>
                  <div className="sv-field sv-field-grow">
                    <label className="sv-label">Description</label>
                    <input type="text" placeholder="Optional description" value={newItem.description} onChange={e => setNewItem({ ...newItem, description: e.target.value })} className="sv-input" />
                  </div>
                </div>
                <div className="sv-form-actions">
                  <button className="sv-btn sv-btn-primary" onClick={() => handleAddItem(selectedCat)}>Add Service</button>
                  <button className="sv-btn sv-btn-ghost" onClick={() => setAddingItem(null)}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Services Grid */}
          {activeCatItems.length === 0 && addingItem !== selectedCat ? (
            <div className="sv-empty">
              <div className="sv-empty-icon">{activeCatIcon}</div>
              <p className="sv-empty-text">No services in {activeCat.name}</p>
              {isAdmin && (
                <button className="sv-btn sv-btn-primary" onClick={() => { setAddingItem(selectedCat); setNewItem({ name: '', description: '' }); }}>
                  Add First Service
                </button>
              )}
            </div>
          ) : (
            <div className="sv-grid">
              {activeCatItems.map(item => {
                const itemRates = getItemRates(item.id);
                const isOpen = expandedItems[item.id];
                const isEditingThis = editingItem && editingItem.id === item.id;
                const bestRate = itemRates.length > 0 ? Math.min(...itemRates.map(r => calcEffective(r))) : null;

                return (
                  <div key={item.id} className={`sv-card ${isOpen ? 'sv-card-expanded' : ''}`}>
                    {/* Card Media */}
                    <div className="sv-card-media">
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.name} className="sv-card-img" />
                      ) : (
                        <div className="sv-card-placeholder">
                          <span>{activeCatIcon}</span>
                        </div>
                      )}
                      {bestRate !== null && (
                        <div className="sv-price-tag">
                          <span className="sv-price-from">from</span>
                          <span className="sv-price-val">${bestRate.toFixed(0)}</span>
                        </div>
                      )}
                      {isAdmin && (
                        <div className="sv-card-overlay">
                          <label className="sv-overlay-btn" title="Upload image">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
                            <input type="file" accept="image/*" className="sv-hidden-input" onChange={e => handleImageUpload('item', item.id, e.target.files[0])} />
                          </label>
                          {uploadingImage === `item-${item.id}` && <span className="sv-uploading-dot" />}
                          <button className="sv-overlay-btn" onClick={e => { e.stopPropagation(); setEditingItem({ id: item.id, name: item.name, description: item.description || '' }); }} title="Edit">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                          <button className="sv-overlay-btn sv-overlay-btn-danger" onClick={e => { e.stopPropagation(); handleDeleteItem(item.id); }} title="Delete">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Card Body */}
                    <div className="sv-card-body">
                      {isEditingThis ? (
                        <div className="sv-inline-edit">
                          <input type="text" value={editingItem.name} onChange={e => setEditingItem({ ...editingItem, name: e.target.value })} className="sv-input sv-input-compact" autoFocus />
                          <div className="sv-inline-btns">
                            <button className="sv-btn-icon sv-btn-icon-ok" onClick={() => handleUpdateItem(item)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                            </button>
                            <button className="sv-btn-icon sv-btn-icon-cancel" onClick={() => setEditingItem(null)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <h4 className="sv-card-title">{item.name}</h4>
                      )}
                      {!isEditingThis && item.description && <p className="sv-card-desc">{item.description}</p>}
                      {(item.length_ft > 0 || item.max_players > 0) && (
                        <div className="sv-card-meta">
                          {item.length_ft > 0 && <span className="sv-meta-tag">📏 {item.length_ft}ft</span>}
                          {item.max_players > 0 && <span className="sv-meta-tag">👥 Max {item.max_players}</span>}
                        </div>
                      )}
                    </div>

                    {/* Rates Toggle Bar */}
                    <div className="sv-card-rates-bar" onClick={() => setExpandedItems(p => ({ ...p, [item.id]: !p[item.id] }))}>
                      <span className="sv-rates-label">
                        {itemRates.length > 0
                          ? `${itemRates.length} rate plan${itemRates.length !== 1 ? 's' : ''}`
                          : 'No rates yet'}
                      </span>
                      <svg className={`sv-chevron-icon ${isOpen ? 'sv-chevron-open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                    </div>

                    {/* Expanded Rates */}
                    {isOpen && (
                      <div className="sv-card-rates">
                        {isAdmin && (
                          <button className="sv-btn sv-btn-sm sv-btn-outline" onClick={() => { setAddingRateFor(item.id); setNewRate({ rate_type: rateTypes[0]?.key || '', rate_amount: '', discount_type: '', discount_value: 0 }); }}>
                            + Add Rate
                          </button>
                        )}

                        {addingRateFor === item.id && (
                          <div className="sv-rate-inline">
                            <select value={newRate.rate_type} onChange={e => setNewRate({ ...newRate, rate_type: e.target.value })} className="sv-input sv-select-sm">
                              {rateTypes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                            </select>
                            <div className="sv-money-wrap">
                              <span className="sv-money-sign">$</span>
                              <input type="number" placeholder="0.00" value={newRate.rate_amount} onChange={e => setNewRate({ ...newRate, rate_amount: e.target.value })} className="sv-input sv-input-money" min="0" step="0.01" />
                            </div>
                            <select value={newRate.discount_type} onChange={e => setNewRate({ ...newRate, discount_type: e.target.value })} className="sv-input sv-select-sm">
                              <option value="">No discount</option>
                              {discountTypes.filter(d => d.key).map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
                            </select>
                            {newRate.discount_type && (
                              <div className="sv-money-wrap">
                                <span className="sv-money-sign">{discountSymbolMap[newRate.discount_type] || ''}</span>
                                <input type="number" placeholder="0" value={newRate.discount_value} onChange={e => setNewRate({ ...newRate, discount_value: e.target.value })} className="sv-input sv-input-money" min="0" step="0.01" />
                              </div>
                            )}
                            <button className="sv-btn-icon sv-btn-icon-ok" onClick={() => handleAddRate(item.id)} title="Save">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                            </button>
                            <button className="sv-btn-icon sv-btn-icon-cancel" onClick={() => setAddingRateFor(null)} title="Cancel">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          </div>
                        )}

                        {itemRates.length === 0 && addingRateFor !== item.id && (
                          <p className="sv-no-rates">No rates configured.{isAdmin && ' Add a rate plan to set pricing.'}</p>
                        )}

                        {itemRates.length > 0 && (
                          <div className="sv-rate-list">
                            {itemRates.map(rate => {
                              const effective = calcEffective(rate);
                              const hasDiscount = rate.discount_type && rate.discount_value > 0;
                              const isEditingThisRate = editingRate && editingRate.id === rate.id;

                              return (
                                <div key={rate.id} className={`sv-rate-row ${hasDiscount ? 'sv-rate-row-discount' : ''}`}>
                                  {isEditingThisRate ? (
                                    <div className="sv-rate-inline" onClick={e => e.stopPropagation()}>
                                      <select value={editingRate.rate_type} onChange={e => setEditingRate({ ...editingRate, rate_type: e.target.value })} className="sv-input sv-select-sm">
                                        {rateTypes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                                      </select>
                                      <div className="sv-money-wrap">
                                        <span className="sv-money-sign">$</span>
                                        <input type="number" value={editingRate.rate_amount} onChange={e => setEditingRate({ ...editingRate, rate_amount: e.target.value })} className="sv-input sv-input-money" min="0" step="0.01" />
                                      </div>
                                      <select value={editingRate.discount_type || ''} onChange={e => setEditingRate({ ...editingRate, discount_type: e.target.value })} className="sv-input sv-select-sm">
                                        <option value="">No discount</option>
                                        {discountTypes.filter(d => d.key).map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
                                      </select>
                                      {editingRate.discount_type && (
                                        <div className="sv-money-wrap">
                                          <span className="sv-money-sign">{discountSymbolMap[editingRate.discount_type] || ''}</span>
                                          <input type="number" value={editingRate.discount_value} onChange={e => setEditingRate({ ...editingRate, discount_value: e.target.value })} className="sv-input sv-input-money" min="0" step="0.01" />
                                        </div>
                                      )}
                                      <button className="sv-btn-icon sv-btn-icon-ok" onClick={() => handleUpdateRate(rate)} title="Save">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                                      </button>
                                      <button className="sv-btn-icon sv-btn-icon-cancel" onClick={() => setEditingRate(null)} title="Cancel">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                      </button>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="sv-rate-left">
                                        <span className="sv-rate-type-icon">{rateIcons[rate.rate_type]}</span>
                                        <span className="sv-rate-type-name">{rateLabels[rate.rate_type] || rate.rate_type}</span>
                                      </div>
                                      <div className="sv-rate-right">
                                        <div className="sv-rate-prices">
                                          {hasDiscount && <span className="sv-rate-original">${rate.rate_amount.toFixed(0)}</span>}
                                          <span className={`sv-rate-amount ${hasDiscount ? 'sv-rate-amount-green' : ''}`}>${effective.toFixed(2)}</span>
                                          {hasDiscount && (
                                            <span className="sv-discount-chip">
                                              {rate.discount_type === 'percent' ? `-${rate.discount_value}%` : `-$${parseFloat(rate.discount_value).toFixed(0)}`}
                                            </span>
                                          )}
                                        </div>
                                        {isAdmin && (
                                          <div className="sv-rate-acts">
                                          <button className="sv-btn-icon" onClick={() => setEditingRate({ id: rate.id, rate_type: rate.rate_type, rate_amount: rate.rate_amount, discount_type: rate.discount_type || '', discount_value: rate.discount_value || 0 })} title="Edit">
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                          </button>
                                          <button className="sv-btn-icon sv-btn-icon-danger" onClick={() => handleDeleteRate(rate.id)} title="Delete">
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                                          </button>
                                        </div>
                                      )}
                                      </div>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Add Service Card */}
              {isAdmin && addingItem !== selectedCat && (
                <div className="sv-card sv-card-add" onClick={() => { setAddingItem(selectedCat); setNewItem({ name: '', description: '' }); }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  <span>Add Service</span>
                </div>
              )}
            </div>
          )}
        </div>
      ) : categories.length === 0 ? (
        <div className="sv-empty">
          <div className="sv-empty-icon">🏗️</div>
          <p className="sv-empty-text">No service categories yet</p>
          {isAdmin && (
            <button className="sv-btn sv-btn-primary" onClick={() => setAddingCategory(true)}>Create Your First Category</button>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default ServicesConfig;
